<template>
    <LoadingView v-if="!loaded.collection" />
    <div v-else class="flex flex-col gap-8">
        <nav class="flex flex-none gap-4 justify-between items-center rounded-t-lg">
            <div class="flex-none">
                <FormButton
                    color="secondary"
                    icon="fa-chevron-left"
                    @click="prev ? $router.push(prev) : $router.go(-1)">
                    Back
                </FormButton>
            </div>
            <div class="py-4 sm:rounded-tr-lg flex justify-end items-center h-16 gap-2">
                <NotificationSubscription v-if="community?.id" :type="NotificationTypes.DIVISION" :id="community.id" :context="community" />
                <ToggleButton
                    v-if="user.level >= 2"
                    id="publish-toggle"
                    on-color="green"
                    :checked="collection.status == 'active'"
                    @changed="changeStatus" label="Set Publishing Status" />
            </div>
        </nav>
        <header class="flex items-start gap-8">
            <div class="flex flex-col lg:flex-row gap-5 sm:gap-6 flex-1">
                <div class="flex flex-col gap-5 sm:gap-6 flex-1 justify-between">
                    <div class="flex flex-col lg:flex-row gap-5 justify-between flex-none">

                        <div class="flex-1 flex flex-col gap-2 lg:gap-0 order-2 lg:order-1">
                            <h1 class="text-3xl leading-9 font-medium font-headline antialiased  bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text inline-block">
                                {{ collection.meta?.info?.name || collection.kova?.MarketingName }}
                                <span v-if="!['N/S',''].includes(collection?.kova.CustomFields.TextCommWebStatus1)" class="bg-oakwood-gray text-white px-2 rounded-full inline-block text-xs leading-5 relative bottom-1 ml-2">{{ collection?.meta?.info?.callout }}</span>
                            </h1>
                            <dl class="lg:flex gap-2">
                                <dt class="sr-only">
                                    Description
                                </dt>
                                <dd class="mt-1 text-sm leading-5 text-bhav-blue-300 border-r border-r-bhav-blue-50 pr-2">
                                    {{ collection.kova?.Description }}
                                </dd>
                                <dt class="mt-1 text-sm leading-5 text-bhav-blue-300">
                                    Kova Community ID:
                                </dt>
                                <dd class="mt-1 text-sm leading-5 text-bhav-blue-300 border-r border-r-bhav-blue-50 pr-2">
                                    {{ collection.community_rid }}
                                </dd>
                                <dt class="sr-only">
                                    Community ID
                                </dt>
                                <dd class="mt-1 text-sm leading-5 text-bhav-blue-300 border-r border-r-bhav-blue-50 pr-2">
                                    {{ collection.kova?.CommunityID }}
                                </dd>
                                <dt class="sr-only">
                                    Community FID
                                </dt>
                                <dd class="mt-1 text-sm leading-5 text-bhav-blue-300">
                                    {{ collection.kova?.CommunityFID }}
                                </dd>

                            </dl>
                        </div>
                    </div>
                    <section class="flex flex-row w-full gap-2">
                        <h2 class="sr-only col-span-full">Basic Information</h2>
                        <InfoText class="w-full" label="Sales Status" :icon="`fak ${!['Internal', 'Off Market', 'Sold Out','N/S',''].includes(collection.kova.CustomFields.TextCommWebStatus1) ? 'fa-bulb' : 'fa-bulb'}`" :value="FloorplanStatuses.label(collection.kova.CustomFields.TextCommWebStatus1)" :color="!['Internal', 'Off Market', 'Sold Out','N/S',''].includes(collection.kova.CustomFields.TextCommWebStatus1) ? 'green' : 'red'" :bg-color="!['Internal', 'Off Market', 'Sold Out','N/S',''].includes(collection.kova.CustomFields.TextCommWebStatus1) ? 'green' : 'red'" />
                        <InfoText class="w-full" bg-color="gray" label="Agents" icon="fak fa-collection" :value="String(agents?.length || 0)" />
                        <InfoText class="w-full" bg-color="gray" label="Floorplans" icon="fak fa-floorplan" :value="String(floorplans.length)" />
                        <InfoText class="w-full" bg-color="gray" label="Available Homes" icon="fak fa-home-available" :value="String(sales_orders.length)" />
                    </section>

                </div>
                <div class="w-full lg:w-1/5 flex">
                    <img v-if="collection.image" class="object-cove rounded-lg"
                        :src="collection.image" :alt="collection.kova?.Name"
                        :title="collection.kova?.Name">
                    <div v-else class="text-5xl bg-bhav-blue-300 rounded-lg text-white p-8 flex items-center justify-center w-full">
                        <span class="fak fa-collection"></span>
                    </div>
                </div>

            </div>
        </header>
        <TabPanes route="collection-tab" :tabs="tabs" v-slot="{tab}" via="via-white" to="to-white" class="gap-4 py-6">
            <InfoPane :collection="collection" @update="prepare" v-if="tab?.name === 'info'" />
            <OfficePane :collection="collection" @update="prepare" v-if="tab?.name === 'office'" />  
            <SalesTeamPane :agents="agents" @update="indexAgents" v-if="tab?.name === 'agents'" />
            <FloorplanPane :floorplans="floorplans" v-if="tab?.name === 'floorplans' && loaded.floorplans" />  
            <SalesOrderPane :sales_orders="sales_orders" :floorplans="floorplans" v-if="tab?.name === 'homes' && loaded.sales_orders" />  
            <ImagePane :parent="collection" context="collection" @update="(image) => {collection.image = image.media?.url}" v-if="tab?.name === 'photos'" />  
            <VideoPane :item="collection" context="collection" @update="prepare" v-if="tab?.name === 'videos'" />
            <InteractivePane :item="collection" context="collection" @update="prepare" v-if="tab?.name === 'interactive'" />
            <PromotionsPane :id="collection.id" context="collections" v-if="tab?.name === 'promotions'" />
            <LotsPane context="collections" :collections="[collection]" :id="collection.id" v-if="tab?.name === 'lots'"/>
            <LogPane context="collection" v-if="tab?.name === 'logs'" />
    
            <template v-if="tab?.name === 'debugger'">
                <h2>Raw Data</h2>
                <pre>
                    {{ collection }}
                </pre>
            </template>
        </TabPanes>
    </div>
</template>

<script>
import axios from "axios";
import LoadingView from '@/components/LoadingView.vue';
import {FormButton, ToggleButton} from '@/components/form';
import {InfoPane, OfficePane} from '@/components/collections';
import {InteractivePane, ImagePane, VideoPane, FloorplanPane, SalesOrderPane, SalesTeamPane, LotsPane, LogPane, PromotionsPane} from '@/components/shared';
import { mapState, mapActions } from "pinia";
import { useAuthStore } from "@/stores/auth";
import { useMessageStore } from "@/stores/message";
import { handle404 } from "@/helpers/globals";
import TabPanes from "@/components/TabPanes.vue";
import Statuses from "@/enums/Statuses";
import FloorplanStatuses from "@/enums/FloorplanStatuses";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import NotificationTypes from "@/enums/NotificationTypes";
import Community from "@/models/community";
import InfoText from "@/components/media/InfoText.vue";
import Collection from "@/models/collection";

let tabs = [
    {
        name: 'info',
        icon: 'fa-alert',
        title: 'General Information',
        acl: 0
    },
    {
        name: 'office',
        icon: 'fa-chevron',
        title: 'Sales Office',
        acl: 0
    },
    {
        name: 'agents',
        prefixIcon: 'fak',
        icon: 'fa-users',
        title: 'Sales Team',
        acl: 0
    },
    {
        name: 'floorplans',
        icon: 'fa-floorplan',
        title: 'Floorplans',
        acl: 0
    },
    {
        name: 'homes',
        icon: 'fa-home-available',
        title: 'Available Homes',
        acl: 0
    },
    {
        name: 'photos',
        icon: 'fa-photo',
        title: 'Images',
        acl: 0
    },
    {
        name: 'videos',
        icon: 'fa-video',
        title: 'Videos',
        acl: 0
    },
    {
        name: 'interactive',
        icon: 'fa-3d',
        title: 'Interactives',
        acl: 0
    },
    {
        name: 'promotions',
        icon: 'fa-dollar',
        title: 'Promotions',
        acl: 2
    },
    {
        name: 'lots',
        icon: 'fa-lot',
        title: 'Lots',
        acl: 0
    },
    {
        name: 'logs',
        icon: 'fa-history',
        title: 'History',
        acl: 1
    },
    {
        name: 'debugger',
        icon: 'fa-debug',
        title: 'Debugger',
        acl: 2
    },
];

export default {
    head() {
        return {
            title: () => `${this.$route.meta.title} %separator ${this.collection?.meta?.info?.name}`
        }
    },
    setup() {
        return {
            FloorplanStatuses
        }
    },
    data() {
        
        return {
            revert: null,
            collection: new Collection({id: this.$route.params.id}),
            community: new Community({}),
            floorplans: [],
            sales_orders: [],
            agents: [],
            loaded: {
                collection: false,
                floorplans: false,
                sales_orders: false,
                agents: false
            },
            tabs: [],
            prev: false,
            NotificationTypes
        }
    },
    methods: {
        show() {
            axios.get(`collections/${this.collection.id}`)
                .then(res => {
                    if(res.data.meta === null) res.data.meta = {};
                    res.data.meta = {...this.collection?.meta, ...res.data.meta};
                    this.collection = new Collection(res.data);
                    this.community = new Community(this.collection.community);
                    this.loaded.collection = true;
                    this.indexFloorplans();
                    this.indexSalesOrders();
                    this.indexAgents();
                })
                .catch(err => {
                    if(err.response.status === 404)
                        handle404(this, err.response.data.message);
                });
        },
        indexFloorplans() {
            this.loaded.floorplans = false;
            axios.get(`collections/${this.collection.id}/floorplans`)
                .then(res => {
                    this.floorplans = res.data;
                    this.loaded.floorplans = true;
                })
                .catch(err => {
                    console.error(err);
                })
        },
        async indexSalesOrders() {
            this.loaded.sales_orders = false;
            await axios.get(`collections/${this.collection.id}/sales_orders`)
                .then(res => {
                    this.sales_orders = res.data;
                    this.loaded.sales_orders = true;
                })
                .catch(err => {
                    console.error(err);
                })
        },
        async indexAgents() {
            this.loaded.agents = false;
            await axios.get(`collections/${this.collection.id}/agents`)
                .then(res => {
                    this.agents = res.data;
                    this.loaded.agents = true;
                })
                .catch(err => {
                    console.error(err);
                })
        },
        isGeoValueSet(value) {
            return value !== null && value !== undefined && value !== '' && value != '0';
        },
        changeStatus(toggle) {
            if (toggle && (!this.isGeoValueSet(this.collection.meta?.sales_office?.geo?.lat) || !this.isGeoValueSet(this.collection.meta?.sales_office?.geo?.lng))) {
                this.setAlert({
                    text: 'Validation Error: Unable to publish. Latitude and Longitude coordinates are required for the Sales Office location.',
                    level: 'error'
                });
                return;
            }
            this.collection.status = toggle ? 'active' : 'inactive';
            this.save();
        },
        save() {
            this.loaded.collection = true;
            axios.patch(`collections/${this.collection.id}`, this.collection)
                .then(res => {
                    this.collection = new Collection(res.data);
                })
                .catch(e => {
                    debugger;
                    this.collection = new Collection(this.revert);
                    throw e;
                })
                .finally(() => {
                    this.revert = null;
                    this.loaded.collection = true;
                });
        },
        prepare(value) {
            this.revert = this.collection;
            this.collection = value;
            this.save();
        },
        ...mapActions(useMessageStore, ['setAlert'])
    },
    computed: {
        ...mapState(useAuthStore, ['user']),
        lot() {
            let lotId = this.collection.kova?.Lot?.LotID;
            if(!lotId) return '-';
            return parseInt(lotId.split('-').pop());
        },
        statusLabel() {
            return Statuses.label(this.collection.status)
        }
    },
    created() {
        this.show();
        this.tabs = tabs.filter(tab => {
            return this.user.level >= tab.acl
        });
    },
    beforeRouteEnter(to,from,next) {
        next(vm => {
            if(!vm.prev) vm.prev = (from.name && from) ?? false;
        })
    },
    components: {
        NotificationSubscription,
        LoadingView,
        ToggleButton,
        FormButton,
        TabPanes,
        InteractivePane,
        VideoPane,
        ImagePane,
        InfoPane,
        OfficePane,
        SalesOrderPane,
        SalesTeamPane,
        FloorplanPane,
        LotsPane,
        LogPane,
        PromotionsPane,
        InfoText
    },
}
</script>